import ExcelJS from 'exceljs';
import {
  CatalogProductBulkImportBodyParams,
  CustomFieldDefinition,
} from 'src/api/generated';
import { parseCsv } from 'src/modules/shared/utils/csv/parse-csv.util';
/**
 * Represents a single parsed row from an import file.
 * Each key is a column header, and values are the cell contents (or undefined if empty).
 */
export type ParsedRow = Record<string, string | undefined>;

/**
 * Standard product fields supported by the bulk import feature.
 * These fields map to core product properties in the catalog.
 *
 * @see autoMapProductFields for automatic header-to-field matching
 */
export type ProductField =
  | 'id'
  | 'sourceId'
  | 'name'
  | 'description'
  | 'price'
  | 'currency'
  | 'sourceUrl';

/**
 * Normalizes a header string by converting to lowercase and removing non-alphanumeric characters.
 * Used for case-insensitive and format-agnostic header matching.
 *
 * @param header - The header string to normalize
 * @returns The normalized header string
 */
export const normalizeHeader = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Auto-maps standard product fields to their corresponding column headers.
 * Performs fuzzy matching using normalized headers to handle variations in naming.
 *
 * @param headers - Array of column headers from the import file
 * @returns Mapping of product fields to their matched header names, or undefined if no match found
 */
export const autoMapProductFields = (
  headers: string[]
): Partial<Record<ProductField, string>> => {
  const normalized = headers.map((h) => ({
    raw: h,
    norm: normalizeHeader(h),
  }));

  const match = (candidates: string[]): string | undefined => {
    const candidateNorms = candidates.map(normalizeHeader);
    return normalized.find(({ norm }) => candidateNorms.includes(norm))?.raw;
  };

  return {
    id: match(['id']),
    sourceId: match([
      'sourceid',
      'externalid',
      'external_id',
      'id_externo',
      'origen_id',
    ]),
    name: match(['name', 'nombre', 'producto']),
    description: match(['description', 'descripcion', 'detalle']),
    price: match(['price', 'precio']),
    currency: match(['currency', 'moneda']),
    sourceUrl: match(['sourceurl', 'url', 'link']),
  };
};

/**
 * Auto-maps custom field definitions to their corresponding column headers.
 * Uses field key and label as matching candidates with fuzzy matching.
 *
 * @param definitions - Array of custom field definitions
 * @param headers - Array of column headers from the import file
 * @returns Mapping of custom field IDs to their matched header names
 */
export const autoMapCustomFields = (
  definitions: CustomFieldDefinition[],
  headers: string[]
): Record<number, string | undefined> => {
  const normalized = headers.map((h) => ({
    raw: h,
    norm: normalizeHeader(h),
  }));

  const findMatch = (candidates: string[]): string | undefined => {
    const candidateNorms = candidates.map(normalizeHeader);
    return normalized.find(({ norm }) => candidateNorms.includes(norm))?.raw;
  };

  return definitions.reduce<Record<number, string | undefined>>((acc, def) => {
    const match = findMatch([
      def.fieldKey,
      def.fieldLabel || '',
      `${def.fieldKey}`,
    ]);
    acc[def.id] = match;
    return acc;
  }, {});
};

/**
 * Extracts field values from a parsed row using the provided field mapping.
 *
 * @param row - The parsed row data
 * @param mapping - Mapping of product fields to header names
 * @returns Object containing only the mapped field values that exist in the row
 */
export const mapRowWithFields = (
  row: ParsedRow,
  mapping: Partial<Record<ProductField, string | undefined>>
): Partial<Record<ProductField, string>> => {
  const result: Partial<Record<ProductField, string>> = {};

  (Object.keys(mapping) as ProductField[]).forEach((field) => {
    const header = mapping[field];
    if (!header) return;

    const value = row[header];
    if (value !== undefined) {
      result[field] = value;
    }
  });

  return result;
};

/**
 * Parses an Excel file and extracts headers and rows.
 * Handles .xlsx and .xls files using ExcelJS.
 *
 * @param file - The Excel file to parse
 * @returns Object containing headers and parsed rows
 */
export const parseExcel = async (
  file: File
): Promise<{
  headers: string[];
  rows: ParsedRow[];
}> => {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0];
  const headerRow = worksheet.getRow(1);
  const headerValues = Array.isArray(headerRow.values)
    ? headerRow.values.slice(1)
    : [];

  const headers = headerValues
    .map((cell) => (cell ? String(cell).trim() : ''))
    .filter((val) => val.length > 0);

  const rows: ParsedRow[] = [];
  const rowCount = worksheet.rowCount;

  for (let rowIndex = 2; rowIndex <= rowCount; rowIndex += 1) {
    const row = worksheet.getRow(rowIndex);
    const rowData: ParsedRow = {};

    headers.forEach((header, idx) => {
      const cell = row.getCell(idx + 1);
      const cellValue = extractCellValue(cell);

      if (cellValue !== '') {
        rowData[header] = cellValue;
      }
    });

    if (Object.keys(rowData).length > 0) {
      rows.push(rowData);
    }
  }

  return { headers, rows };
};

/**
 * Extracts and normalizes a cell value from an ExcelJS cell.
 * Prefers the text representation if available, falls back to raw value,
 * and returns an empty string if the value is null or undefined.
 *
 * @param cell - The ExcelJS cell to extract from
 * @returns The normalized cell value as a trimmed string
 */
function extractCellValue(cell: ExcelJS.Cell): string {
  const hasText = typeof cell.text === 'string' && cell.text.length > 0;
  const rawValue = hasText ? cell.text : cell.value;

  if (rawValue === undefined || rawValue === null) {
    return '';
  }

  return String(rawValue).trim();
}

/**
 * Parses a text file (CSV/TSV) and extracts headers and rows.
 *
 * @param file - The text file to parse
 * @returns Object containing headers and parsed rows
 */
export const parseTextFile = async (
  file: File
): Promise<{
  headers: string[];
  rows: ParsedRow[];
}> => {
  const text = await file.text();
  const parsed = parseCsv(text);
  return { headers: parsed.headers, rows: parsed.rows };
};

/**
 * Parses a product import file.
 * Automatically detects format based on file extension (.xlsx/.xls = Excel, otherwise CSV/text).
 *
 * @param file - The file to parse
 * @returns Object containing headers and parsed rows
 */
export const parseProductFile = async (file: File) => {
  const isExcel = /\.xlsx?$/i.test(file.name);
  return isExcel ? parseExcel(file) : parseTextFile(file);
};

/**
 * Builds a product bulk import payload from a parsed row.
 * Maps standard and custom fields to their corresponding values from the row.
 *
 * @param row - The parsed row data
 * @param fieldMapping - Mapping of product fields to header names
 * @param customFieldMapping - Mapping of custom field IDs to header names
 * @param customFieldDefinitions - Array of custom field definitions
 * @returns A product bulk import item with all mapped fields
 */
export const buildProductPayload = (
  row: ParsedRow,
  fieldMapping: Partial<Record<ProductField, string | undefined>>,
  customFieldMapping: Record<number, string | undefined>,
  customFieldDefinitions: CustomFieldDefinition[]
): CatalogProductBulkImportBodyParams['items'][number] => {
  const { id, sourceId, name, description, price, currency, sourceUrl } =
    mapRowWithFields(row, fieldMapping);

  const customFieldsEntries = customFieldDefinitions.reduce<
    Record<string, unknown>
  >((acc, definition) => {
    const header = customFieldMapping[definition.id];
    if (!header) return acc;
    const value = row[header];
    if (value !== undefined) {
      acc[definition.fieldKey] = value;
    }
    return acc;
  }, {});

  const payload = {
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    ...(price ? { price } : {}),
    ...(currency ? { currency } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(sourceId ? { sourceId } : {}),
  } as CatalogProductBulkImportBodyParams['items'][number];

  if (id) {
    const parsedId = Number(id);
    if (!Number.isNaN(parsedId)) {
      payload.id = parsedId;
    }
  }

  if (Object.keys(customFieldsEntries).length > 0) {
    payload.customFields = customFieldsEntries;
  }

  return payload;
};
